import { useEffect, useRef } from 'react';
import { useEditor } from '../../state/editorStore';
import { playbackClock } from '../../playback/clock';
import { transitionEffect, ramp } from '../../lib/transitions';

/**
 * Renders the active timeline transition on the stage. Driven by the playback
 * clock (not React re-renders): a colour overlay for fade/flash, a transform on
 * the video for zoom/slide. Shares transitionEffect() with the (future) export.
 */
export function TransitionOverlay() {
  const transitions = useEditor((s) => s.transitions);
  const brolls = useEditor((s) => s.brolls);
  const overlayRef = useRef<HTMLDivElement>(null);
  const model = useRef(transitions);
  model.current = transitions;
  const brollModel = useRef(brolls);
  brollModel.current = brolls;

  useEffect(() => {
    // Zoom/slide must move the whole visual SCENE as one, not just the base
    // video: the base video, the person cut-out (depth) and B-roll are separate
    // sibling layers, so we transform all of them together (otherwise the
    // subject stays put while the background zooms). Captions and guides are
    // overlays and stay fixed - matching the export, where captions are drawn
    // after the transform is baked in. A blurred backdrop fills revealed areas;
    // whip motion blur rides on the same layers via filter.
    const stage = () => overlayRef.current?.closest<HTMLElement>('.studio-stage') ?? null;
    const footage = (): HTMLElement[] => {
      const s = stage();
      return s
        ? Array.from(s.querySelectorAll<HTMLElement>('.studio-content, .depth-layer, .broll-layer'))
        : [];
    };
    const baseLayers = (): HTMLElement[] => {
      const s = stage();
      return s ? Array.from(s.querySelectorAll<HTMLElement>('.studio-content, .depth-layer')) : [];
    };
    const brollLayer = () => stage()?.querySelector<HTMLElement>('.broll-layer') ?? null;
    const backdrop = () => stage()?.querySelector<HTMLElement>('.studio-video-bg') ?? null;
    const setReveal = (bg: HTMLElement | null, sharp: boolean) => {
      if (!bg) return;
      bg.style.filter = sharp ? 'none' : '';
      bg.style.transform = sharp ? 'none' : '';
    };
    const reset = (els: HTMLElement[]) => els.forEach((el) => {
      el.style.transform = '';
      el.style.filter = '';
      el.style.opacity = '';
    });
    // Clear everything this component may have set, so no inline style lingers.
    const clearAll = () => {
      reset(footage());
      const bl = brollLayer();
      if (bl) bl.style.zIndex = '';
      setReveal(backdrop(), false);
    };
    // How the OUTGOING layer animates out at a boundary (mirrors the export's
    // outgoingAnim): slide translates off, fade drops opacity, zoom scales up +
    // fades (a zoom-through).
    const outgoingCss = (kind: string, p: number): { transform: string; opacity: string } => {
      const r = ramp(p);
      if (kind === 'slide') return { transform: `translateX(${(-100 * r).toFixed(2)}%)`, opacity: '' };
      if (kind === 'fade') return { transform: '', opacity: (1 - r).toFixed(3) };
      return { transform: `scale(${(1 + 0.35 * r).toFixed(3)})`, opacity: (1 - r).toFixed(3) };
    };
    const BOUNDARY = ['slide', 'fade', 'zoomIn', 'zoomOut'];
    const apply = (t: number) => {
      const active = model.current.find((tr) => t >= tr.startMs && t <= tr.startMs + tr.durationMs);
      const el = overlayRef.current;
      if (!active) {
        if (el) { el.style.opacity = '0'; el.style.background = 'transparent'; }
        clearAll();
        return;
      }
      const p = Math.min(1, Math.max(0, (t - active.startMs) / Math.max(1, active.durationMs)));
      const bl = brollLayer();

      // A slide/fade/zoom straddling a COVER B-roll boundary is a REVEAL between
      // the base video and the B-roll (mirrors the export compositor).
      if (BOUNDARY.includes(active.kind)) {
        const inWin = (ms: number) => ms >= active.startMs - 1 && ms <= active.startMs + active.durationMs + 1;
        const covers = brollModel.current.filter((b) => b.mode === 'cover');
        const startBroll = covers.find((b) => inWin(b.startMs));
        const endBroll = startBroll ? undefined : covers.find((b) => inWin(b.startMs + b.durationMs));
        if (startBroll || endBroll) {
          if (el) { el.style.opacity = '0'; el.style.background = 'transparent'; }
          const css = outgoingCss(active.kind, p);
          if (startBroll) {
            // Base animates out to reveal the B-roll: drop the B-roll below base.
            reset([bl].filter(Boolean) as HTMLElement[]);
            if (bl) bl.style.zIndex = '0';
            baseLayers().forEach((box) => { box.style.transform = css.transform; box.style.opacity = css.opacity; });
          } else {
            // B-roll animates out to reveal the base beneath (natural order).
            reset(baseLayers());
            if (bl) { bl.style.zIndex = ''; bl.style.transform = css.transform; bl.style.opacity = css.opacity; }
          }
          setReveal(backdrop(), false);
          return;
        }
      }

      // Not a boundary reveal → the previous whole-scene behavior: fade/flash =
      // colour overlay; slide/zoom = transform the whole scene over the backdrop.
      const fx = transitionEffect(active.kind, p);
      if (el) { el.style.background = fx.overlay || 'transparent'; el.style.opacity = fx.overlay ? '1' : '0'; }
      if (bl) bl.style.zIndex = '';
      footage().forEach((box) => { box.style.transform = fx.transform; box.style.filter = fx.filter; box.style.opacity = ''; });
      setReveal(backdrop(), active.kind === 'slide');
    };
    const unsub = playbackClock.subscribe(apply);
    apply(playbackClock.timeMs());
    return () => {
      unsub();
      clearAll();
    };
  }, []);

  return <div ref={overlayRef} className="studio-transition-overlay" />;
}
